import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { formatDate } from "@/lib/dates";
import { useT } from "@/i18n";
import { toast } from "sonner";
import { Upload, FileText, History, AlertTriangle, CheckCircle2 } from "lucide-react";

const money = (v) => (v === null || v === undefined ? "—" : Number(v).toFixed(2));

function SummaryCards({ t, data, testid, incomplete }) {
  const cards = [
    ["total", data.total, "text-slate-800"],
    ["new_records", data.new_count, "text-green-700"],
    ["duplicates", data.duplicates_count, "text-amber-700"],
    ["invalid", data.invalid_count, "text-red-700"],
    ["skipped", data.skipped_count, "text-slate-600"],
  ];
  if (incomplete !== undefined) cards.splice(2, 0, ["incomplete", incomplete, "text-amber-600"]);
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid={testid}>
      {cards.map(([key, val, cls]) => (
        <div key={key} className="bg-white border border-slate-200 rounded-lg p-3" data-testid={`${testid}-${key}`}>
          <div className={`text-xl font-bold ${cls}`}>{val}</div>
          <div className="text-[11px] text-slate-500 mt-0.5">{t(`import.${key}`)}</div>
        </div>
      ))}
    </div>
  );
}

const thCls = "text-left text-[11px] uppercase tracking-wide text-slate-500 font-semibold px-3 py-2";
const tdCls = "px-3 py-2 text-sm text-slate-700 border-t border-slate-100";

function CustomerTable({ t, rows, testid, skip }) {
  if (!rows.length) return null;
  return (
    <div className="overflow-x-auto border border-slate-200 rounded-lg bg-white" data-testid={testid}>
      <table className="w-full">
        <thead><tr className="bg-slate-50">
          <th className={thCls}>{t("import.col_name")}</th>
          <th className={thCls}>{t("import.col_legacy_id")}</th>
          <th className={thCls}>{t("import.col_reg")}</th>
          <th className={thCls}>{t("import.col_vat")}</th>
          <th className={thCls}>{t("import.col_country")}</th>
          {skip && <th className={thCls}>{t("import.reason_label")}</th>}
        </tr></thead>
        <tbody>
          {rows.map((c, i) => (
            <tr key={i} data-testid={`${testid}-row-${i}`}>
              <td className={tdCls}>
                {c.name || "—"}
                {c.incomplete && <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded border bg-amber-50 text-amber-700 border-amber-200">{t("import.incomplete")}</span>}
              </td>
              <td className={tdCls}>{c.legacy_id || "—"}</td>
              <td className={tdCls}>{c.registration_number || "—"}</td>
              <td className={tdCls}>{c.vat_number || "—"}</td>
              <td className={tdCls}>{c.country || "—"}</td>
              {skip && (
                <td className={tdCls}>
                  <span className="text-red-700">{t(`import.reason.${c.reason}`)}</span>
                  {c.matched_with && <div className="text-[11px] text-slate-500">{t("import.matched_with")}: {c.matched_with.name} {c.matched_with.registration_number || c.matched_with.vat_number || ""}</div>}
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function InvoiceTable({ t, rows, testid, skip, clientOf }) {
  if (!rows.length) return null;
  return (
    <div className="overflow-x-auto border border-slate-200 rounded-lg bg-white" data-testid={testid}>
      <table className="w-full">
        <thead><tr className="bg-slate-50">
          <th className={thCls}>{t("import.col_invoice_no")}</th>
          <th className={thCls}>{t("import.col_date")}</th>
          <th className={thCls}>{t("import.col_type")}</th>
          <th className={thCls}>{t("import.col_customer")}</th>
          {clientOf && <th className={thCls}>{t("import.col_client_link")}</th>}
          <th className={`${thCls} text-right`}>{t("import.col_taxable")}</th>
          <th className={`${thCls} text-right`}>{t("import.col_vat_amount")}</th>
          <th className={`${thCls} text-right`}>{t("import.col_gross")}</th>
          <th className={thCls}>{t("import.col_currency")}</th>
          {skip && <th className={thCls}>{t("import.reason_label")}</th>}
        </tr></thead>
        <tbody>
          {rows.map((inv, i) => (
            <tr key={i} data-testid={`${testid}-row-${i}`}>
              <td className={tdCls}>
                {inv.id ? (
                  <Link to={`/imported-invoices/${inv.id}`} data-testid={`imp-inv-link-${inv.invoice_no}`}
                    className="text-amber-700 hover:underline font-semibold">
                    {inv.invoice_no}
                  </Link>
                ) : (inv.invoice_no || "—")}
              </td>
              <td className={tdCls}>{formatDate(inv.invoice_date)}</td>
              <td className={tdCls}>{inv.invoice_type || "—"}</td>
              <td className={tdCls}>{inv.customer_name || inv.customer_legacy_id || "—"}</td>
              {clientOf && (
                <td className={tdCls}>
                  {clientOf(inv) ? (
                    <Link to={`/clients/${clientOf(inv)}`} data-testid={`invoice-client-link-${inv.invoice_no}`}
                      className="text-amber-700 hover:underline font-semibold">
                      {t("clients.view")}
                    </Link>
                  ) : "—"}
                </td>
              )}
              <td className={`${tdCls} text-right`}>{money(inv.taxable_value)}</td>
              <td className={`${tdCls} text-right`}>{money(inv.vat_amount)}</td>
              <td className={`${tdCls} text-right`}>{money(inv.gross_total)}</td>
              <td className={tdCls}>{inv.currency || "—"}</td>
              {skip && <td className={tdCls}><span className="text-red-700">{t(`import.reason.${inv.reason}`)}</span></td>}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function Import() {
  const { t } = useT();
  const [tab, setTab] = useState("new");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [archive, setArchive] = useState(null);
  const [archiveQ, setArchiveQ] = useState("");
  const [batches, setBatches] = useState(null);
  const [clients, setClients] = useState([]);

  useEffect(() => {
    if (tab === "archive") {
      api.get("/imported-invoices").then(({ data }) => setArchive(data)).catch(() => setArchive([]));
      api.get("/clients").then(({ data }) => setClients(data)).catch(() => setClients([]));
    }
    if (tab === "batches") api.get("/import/batches").then(({ data }) => setBatches(data)).catch(() => setBatches([]));
  }, [tab]);

  const byBatchLegacy = {}, byLegacyAny = {}, byReg = {}, byVat = {};
  clients.forEach((c) => {
    if (c.legacy_id) {
      byBatchLegacy[`${c.import_batch || ""}|${c.legacy_id}`] = c.id;
      byLegacyAny[c.legacy_id] = byLegacyAny[c.legacy_id] || c.id;
    }
    if (c.registration_number) byReg[c.registration_number] = c.id;
    if (c.vat_number) byVat[c.vat_number] = c.id;
  });
  // Source CustomerID is authoritative within its import batch; a CustomerID
  // owned by another client must never be re-linked via reg/VAT fallback.
  const clientOf = (inv) => {
    const leg = (inv.customer_legacy_id || "").trim();
    if (leg) {
      const exact = byBatchLegacy[`${inv.import_batch || ""}|${leg}`];
      if (exact) return exact;
      if (byLegacyAny[leg]) return null;
    }
    return byReg[inv.customer_registration_number] || byVat[inv.customer_vat_number] || null;
  };

  const searchArchive = async (q) => {
    setArchiveQ(q);
    const { data } = await api.get("/imported-invoices", { params: { q } });
    setArchive(data);
  };

  const errMsg = (e) => {
    const d = e?.response?.data?.detail || "";
    if (d.includes("parse")) return t("import.error_parse");
    if (d.includes("No customers")) return t("import.error_empty");
    return d || t("import.error_parse");
  };

  const analyze = async () => {
    if (!file) return;
    setBusy(true);
    setPreview(null);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/import/isaf/preview", fd);
      setPreview(data);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const confirm = async () => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/import/isaf/confirm", fd);
      setResult(data.batch);
      setPreview(null);
      setFile(null);
      toast.success(t("import.result_title"));
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const tabBtn = (key, icon, label) => {
    const Icon = icon;
    return (
      <button key={key} data-testid={`import-tab-${key}`} onClick={() => setTab(key)}
        className={`flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-md transition-colors ${tab === key ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}>
        <Icon className="h-4 w-4" /> {label}
      </button>
    );
  };

  return (
    <div className="space-y-6" data-testid="import-page">
      <div>
        <h1 className="font-serif text-3xl text-slate-900" data-testid="import-title">{t("import.title")}</h1>
        <p className="text-sm text-slate-500 mt-1">{t("import.subtitle")}</p>
      </div>

      <div className="flex gap-2" data-testid="import-tabs">
        {tabBtn("new", Upload, t("import.tab_new"))}
        {tabBtn("archive", FileText, t("import.tab_archive"))}
        {tabBtn("batches", History, t("import.tab_batches"))}
      </div>

      {tab === "new" && (
        <div className="space-y-6">
          <div className="bg-white border border-slate-200 rounded-lg p-6 space-y-4" data-testid="import-upload-card">
            <div className="flex flex-wrap items-center gap-3">
              <label className="inline-flex items-center gap-2 px-4 py-2 border border-slate-300 rounded-md text-sm font-semibold text-slate-700 hover:border-slate-800 cursor-pointer transition-colors">
                <Upload className="h-4 w-4" /> {t("import.choose_file")}
                <input type="file" accept=".xml" className="hidden" data-testid="import-file-input"
                  onChange={(e) => { setFile(e.target.files[0] || null); setPreview(null); setResult(null); }} />
              </label>
              <span className="text-sm text-slate-600" data-testid="import-file-name">{file ? file.name : t("import.file_hint")}</span>
              <button onClick={analyze} disabled={!file || busy} data-testid="import-analyze-button"
                className="ml-auto px-4 py-2 bg-slate-900 text-white text-sm font-semibold rounded-md hover:bg-slate-800 disabled:opacity-40 transition-colors">
                {busy && !preview ? t("import.analyzing") : t("import.analyze")}
              </button>
            </div>
            <p className="text-[11px] text-slate-400 flex items-center gap-1.5">
              <AlertTriangle className="h-3 w-3" /> {t("import.no_changes")}
            </p>
          </div>

          {result && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-6" data-testid="import-result">
              <div className="flex items-center gap-2 text-green-800 font-semibold mb-3">
                <CheckCircle2 className="h-5 w-5" /> {t("import.result_title")} — {result.filename}
              </div>
              <div className="grid grid-cols-3 gap-3 text-sm">
                <div data-testid="import-result-customers">{t("import.result_customers")}: <b>{result.customers_created}</b></div>
                <div data-testid="import-result-invoices">{t("import.result_invoices")}: <b>{result.invoices_created}</b></div>
                <div data-testid="import-result-skipped">{t("import.result_skipped")}: <b>{result.customers_skipped + result.invoices_skipped}</b></div>
              </div>
            </div>
          )}

          {preview && (
            <div className="space-y-6" data-testid="import-preview">
              <div className="flex items-center justify-between">
                <h2 className="font-serif text-xl text-slate-800">{t("import.preview_title")}</h2>
                <button onClick={confirm} disabled={busy} data-testid="import-confirm-button"
                  className="px-5 py-2.5 bg-amber-600 text-white text-sm font-semibold rounded-md hover:bg-amber-700 disabled:opacity-40 transition-colors">
                  {busy ? t("import.importing") : t("import.confirm")}
                </button>
              </div>

              <section className="space-y-3">
                <h3 className="text-sm font-bold uppercase tracking-wide text-slate-600">{t("import.customers")}</h3>
                <SummaryCards t={t} data={preview.customers} testid="import-customers-summary" incomplete={preview.customers.incomplete_count} />
                <CustomerTable t={t} rows={preview.customers.duplicates} testid="import-customers-duplicates" skip />
                <CustomerTable t={t} rows={preview.customers.invalid} testid="import-customers-invalid" skip />
                <CustomerTable t={t} rows={preview.customers.new} testid="import-customers-new" />
              </section>

              <section className="space-y-3">
                <h3 className="text-sm font-bold uppercase tracking-wide text-slate-600">{t("import.invoices")}</h3>
                <SummaryCards t={t} data={preview.invoices} testid="import-invoices-summary" />
                <InvoiceTable t={t} rows={preview.invoices.duplicates} testid="import-invoices-duplicates" skip />
                <InvoiceTable t={t} rows={preview.invoices.invalid} testid="import-invoices-invalid" skip />
                <InvoiceTable t={t} rows={preview.invoices.new} testid="import-invoices-new" />
              </section>
            </div>
          )}
        </div>
      )}

      {tab === "archive" && (
        <div className="space-y-4" data-testid="import-archive">
          <input value={archiveQ} onChange={(e) => searchArchive(e.target.value)} placeholder={t("import.search_archive")}
            data-testid="import-archive-search"
            className="w-full max-w-md px-3 py-2 border border-slate-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-slate-800" />
          {archive === null ? null : archive.length === 0 ? (
            <div className="text-sm text-slate-500 py-8 text-center" data-testid="import-archive-empty">{t("import.empty_archive")}</div>
          ) : (
            <InvoiceTable t={t} rows={archive} testid="import-archive-table" clientOf={clientOf} />
          )}
        </div>
      )}

      {tab === "batches" && (
        <div data-testid="import-batches">
          {batches === null ? null : batches.length === 0 ? (
            <div className="text-sm text-slate-500 py-8 text-center" data-testid="import-batches-empty">{t("import.empty_batches")}</div>
          ) : (
            <div className="overflow-x-auto border border-slate-200 rounded-lg bg-white" data-testid="import-batches-table">
              <table className="w-full">
                <thead><tr className="bg-slate-50">
                  <th className={thCls}>{t("import.batch_file")}</th>
                  <th className={thCls}>{t("import.batch_date")}</th>
                  <th className={`${thCls} text-right`}>{t("import.result_customers")}</th>
                  <th className={`${thCls} text-right`}>{t("import.result_invoices")}</th>
                  <th className={`${thCls} text-right`}>{t("import.result_skipped")}</th>
                </tr></thead>
                <tbody>
                  {batches.map((b) => (
                    <tr key={b.id} data-testid={`import-batch-${b.id}`}>
                      <td className={tdCls}>{b.filename}</td>
                      <td className={tdCls}>{(b.imported_at || "").slice(0, 16).replace("T", " ")}</td>
                      <td className={`${tdCls} text-right`}>{b.customers_created}</td>
                      <td className={`${tdCls} text-right`}>{b.invoices_created}</td>
                      <td className={`${tdCls} text-right`}>{b.customers_skipped + b.invoices_skipped}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
